/**
 * Library of developer utility functions
 */
const DevUtils = (function () {
    const _DEVMODE = window.localStorage.getItem('devMode') === 'true';

    if (_DEVMODE) {
        // if we are in DevMode, log all API requests/responses
        _logXhr();
    }

    return {
        getBackgroundImageSize: _getBackgroundImageSize,
        isDevMode: _isDevMode,
        isElementOffRightEdge: _isElementOffRightEdge,
        logDebug: _logDebug,
        logError: _logError,
        logGroupCollapsed: _logGroupCollapsed,
        logGroupEnd: _logGroupEnd,
        logVerbose: _logVerbose,
        logWarn: _logWarn
    };

    function _isDevMode() {
        return _DEVMODE;
    }

    /**
     * Function to determine if a given element is running off the right edge of the screen (or modal dialog)
     * @param {any} elem - element to test
     * @returns true if element is running off the edge of the screen
     */
    function _isElementOffRightEdge(elem) {
        let docViewLeft = $(window).scrollLeft();
        let docViewRight = docViewLeft + $(window).width();

        let modal = $(elem).closest('.ah-modal');
        if ($(modal).length > 0) {
            docViewLeft = $(modal).offset().left;
            docViewRight = docViewLeft + $(modal).width();
        }

        let elemLeft = $(elem).offset().left;
        let elemRight = elemLeft + $(elem).width();

        return elemRight >= docViewRight;
    }

    /**
     * Write a console.log() message to browser console, but only if app is in DEV mode (LocalStorage DevMode=true)
     * @param {string} message - the log message to write
     * @param {string} [prefix] - text to prefix to the message (e.g. "A11yUtil.setFocus()")
     * @param {object} [messageObj] - optional object (or array of objects) to display in log message
     * @param {string} [color=gray] - optional color to use for the debug message
     */
    function _logDebug(message, prefix, messageObj, color) {
        if (_isDevMode()) {
            color = color ? 'color:' + color : 'color:gray';
            console.log('%c' + _getLogEntryPrefix(prefix) + message, color, messageObj ? messageObj : '');  // NOSONAR
        }
    }

    /**
     * Write a console.error() message to browser console
     * @param {string} message - the log message to write
     * @param {string} [prefix] - text to prefix to the message (e.g. "A11yUtil.setFocus()")
     */
    function _logError(message, prefix) {
        console.error(_getLogEntryPrefix(prefix) + message);    // NOSONAR
    }

    /**
     * Write a console.groupCollapsed() message to browser console, but only if app is in DEV mode (LocalStorage DevMode=true)
     * @param {string} message - the log message to write
     * @param {string} [prefix] - text to prefix to the message (e.g. "A11yUtil.setFocus()")
     * @param {string} [color=gray] - optional color to use for the debug message
     */
    function _logGroupCollapsed(message, prefix, color) {
        if (_isDevMode()) {
            color = color ? 'color:' + color : 'color:gray';
            console.groupCollapsed('%c' + _getLogEntryPrefix(prefix) + message, color);  // NOSONAR
        }
    }

    /**
     * Write a console.groupEnd() message to browser console
     */
    function _logGroupEnd() {
        if (_isDevMode()) {
            console.groupEnd();  // NOSONAR
        }
    }

    /**
     * Write a console.debug() message to browser console, but only if app is in DEV mode (LocalStorage DevMode=true). These
     * log messages can be seen by turning on the Verbose log level in dev tools. Use this one for log messages which we think
     * we don't want to see that often (i.e. use this to avoid cluttering up the console with extremely minor log messages)
     * @param {string} message - the log message to write
     * @param {string} [prefix] - text to prefix to the message (e.g. "A11yUtil.setFocus()")
     * @param {object} [messageObj] - optional object (or array of objects) to display in log message
     * @param {string} [color=gray] - optional color to use for the debug message
     */
    function _logVerbose(message, prefix, messageObj, color) {
        if (_isDevMode()) {
            color = color ? 'color:' + color : 'color:gray';
            console.debug('%c' + _getLogEntryPrefix(prefix) + message, color, messageObj ? messageObj : '');  // NOSONAR
        }
    }

    /**
     * Write a console.warn() message to browser console
     * @param {string} message - the log message to write
     * @param {string} [prefix] - text to prefix to the message (e.g. "A11yUtil.setFocus()")
     */
    function _logWarn(message, prefix) {
        console.warn(_getLogEntryPrefix(prefix) + message); // NOSONAR
    }

    /**
     * Images defined in CSS as a background-image are hard to set a dynamic size for, so we have to determine the
     * size of the background-image programmatically and then use jquery/javascript to dyamically resize. This
     * function returns the width and height of a background image, given the element containing the image url.
     * This code was found on stackoverflow https://stackoverflow.com/a/12784180
     * @param {string} el the DOM element where the CSS background image is defined
     * @returns promise containing an object with width and height
     */
    function _getBackgroundImageSize(el) {
        var imageUrl = $(el).css('background-image').match(/^url\(["']?(.+?)["']?\)$/);
        var dfd = new $.Deferred();

        if (imageUrl) {
            var image = new Image();
            image.onload = dfd.resolve;
            image.onerror = dfd.reject;
            image.src = imageUrl[1];
        } else {
            dfd.reject();
        }

        return dfd.then(function () {
            return { width: this.width, height: this.height };
        });
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _getLogEntryPrefix(prefix) {
        let _prefix;

        if (prefix) {
            _prefix = prefix + ': ';
        } else {
            _prefix = '';
        }

        return _prefix;
    }

    function _logXhr() {
        $(document).ajaxComplete(function (event, xhr, settings) {
                const urlString = settings.url;
                const urlArray = urlString.split('?');
                const pathname = _getPathNameFromUrlString(urlString);
                const urlSearchParams = new URLSearchParams(urlArray[1]);
                const params = Object.fromEntries(urlSearchParams.entries());

                _logGroupCollapsed('[' + settings.type + '] ' + pathname, 'API', 'blue');

                _logDebug('REQUEST', null, params, 'blue');
                _logDebug('RESPONSE', null, (xhr.responseJSON ? xhr.responseJSON : xhr.responseText), 'blue');
                _logDebug('AJAX PARAMS', null, { event: event, xhr: xhr, settings: settings });

                _logGroupEnd();
        });
    }

    /* return the URL path (the stuff between the hostname/port and the query params, e.g. /Master/LoadReferTypeDDL) */
    function _getPathNameFromUrlString(urlString) {
        let pathname;

        try {
            let url = new URL(urlString);
            pathname = url.pathname;
        } catch (e) {
            pathname = urlString.split('?')[0];
        }

        return pathname.replace('//', '/');
    }
})();
